import React from "react";
import { Card, Typography, Tag, Modal, message, Button } from "antd";
import { CrownOutlined } from "@ant-design/icons";
import { defineMessages, useIntl } from "react-intl";
import { API } from "../apiUrl";
import "./Settings.sass";

const { Text } = Typography;

const i18n = defineMessages({
  premiumActive: { id: "premium.active", defaultMessage: "Premium Active" },
  premium: { id: "premium.premium", defaultMessage: "Premium" },
  daysRemaining: { id: "premium.daysRemaining", defaultMessage: "{days} days remaining" },
  expired: { id: "premium.expired", defaultMessage: "Expired" },
  cancelAutoRenewal: { id: "premium.cancelAutoRenewal", defaultMessage: "Cancel subscription" },
  confirmCancelTitle: { id: "premium.confirmCancelTitle", defaultMessage: "Cancel auto-renewal?" },
  confirmCancelContent: {
    id: "premium.confirmCancelContent",
    defaultMessage:
      "Your premium benefits will remain active until {date}. After that, your subscription will not renew and you will be switched to the Free plan. You can resubscribe at any time.",
  },
  confirmOk: { id: "premium.confirmOk", defaultMessage: "Yes, cancel auto-renewal" },
  keepSubscription: { id: "premium.keepSubscription", defaultMessage: "Keep my subscription" },
  cancelSuccess: {
    id: "premium.cancelSuccess",
    defaultMessage: "Auto-renewal cancelled. You have access until {date}.",
  },
  cancelError: {
    id: "premium.cancelError",
    defaultMessage: "Failed to cancel auto-renewal. Please try again.",
  },
  autoRenewalOff: {
    id: "premium.autoRenewalOff",
    defaultMessage: "Auto-renewal is off",
  },
});

interface Props {
  tier: string;
  expiresAt: number | null;
  isLoggedIn?: boolean;
  onCancelled?: () => void;
}

const PremiumStatus: React.FC<Props> = ({ tier, expiresAt, isLoggedIn, onCancelled }) => {
  const intl = useIntl();
  const [cancelling, setCancelling] = React.useState(false);
  const [cancelledAtPeriodEnd, setCancelledAtPeriodEnd] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const daysLeft = expiresAt
    ? Math.max(0, Math.ceil((expiresAt * 1000 - Date.now()) / 86400000))
    : null;

  const expiryDateStr = expiresAt
    ? new Date(expiresAt * 1000).toLocaleDateString()
    : "";

  const handleCancelAutoRenewal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmOpen(true);
  };

  const doCancel = async () => {
    setConfirmOpen(false);
    setCancelling(true);
    try {
      const token = localStorage.getItem("lumina-jwt");
      if (!token) throw new Error("Not logged in");
      const res = await fetch(`${API}/api/subscription/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Unknown error");
      setCancelledAtPeriodEnd(true);
      message.success(
        intl.formatMessage(i18n.cancelSuccess, { date: expiryDateStr })
      );
      onCancelled?.();
    } catch (e: any) {
      message.error(e.message || intl.formatMessage(i18n.cancelError));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="Settings__panel">
      <Card className="Settings__panelCard Settings__panelCard--premium">
        <CrownOutlined className="Settings__panelIcon" />
        <h5 className="Settings__panelTitle">{intl.formatMessage(i18n.premiumActive)}</h5>
        <Tag color="gold" className="Settings__panelTag">{intl.formatMessage(i18n.premium)}</Tag>
        {daysLeft !== null && (
          <div>
            <Text type="secondary" className="Settings__panelDays">
              {daysLeft > 0
                ? intl.formatMessage(i18n.daysRemaining, { days: daysLeft })
                : intl.formatMessage(i18n.expired)}
            </Text>
            {isLoggedIn && expiresAt && daysLeft > 0 && (
              !cancelledAtPeriodEnd ? (
                <div style={{ marginTop: 4 }}>
                  <Button
                    type="link"
                    size="small"
                    loading={cancelling}
                    onClick={handleCancelAutoRenewal}
                    style={{ fontSize: 12, padding: 0, height: "auto" }}
                  >
                    {intl.formatMessage(i18n.cancelAutoRenewal)}
                  </Button>
                </div>
              ) : (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {intl.formatMessage(i18n.cancelSuccess, { date: expiryDateStr })}
                  </Text>
                </div>
              )
            )}
          </div>
        )}
      </Card>

      <Modal
        title={intl.formatMessage(i18n.confirmCancelTitle)}
        open={confirmOpen}
        onOk={doCancel}
        onCancel={() => setConfirmOpen(false)}
        okText={intl.formatMessage(i18n.confirmOk)}
        cancelText={intl.formatMessage(i18n.keepSubscription)}
        okButtonProps={{ danger: true }}
        confirmLoading={cancelling}
      >
        {intl.formatMessage(i18n.confirmCancelContent, { date: expiryDateStr })}
      </Modal>
    </div>
  );
};

export default PremiumStatus;
